import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuth, useAuthSignOutSubscriber } from '@app/auth';
import { Loader } from '@app/ui/loader';
import { MainLayout } from '@app/layouts';
import { useCardImagePreload } from '@app/application/hooks/useCardImagePreload';
import { resolvedTenantConfig } from '@app/tenant';
import { MATCHING_LOADER_ROUTE, MATCHING_ROUTE } from '@app/matching/constants';

import { SurrogateApplicationPage } from './surrogate-application';
import { UserFormPage } from './surrogate-application/user-form';
import { UploadPhotosPage } from './surrogate-application/upload-photos';
import { AdminUsersPage } from './admin/users';
import { AdminSingleUserPage } from './admin/single-user';
import { AdminSingleUserFormPage } from './admin/single-user-form';
import { AdminSettingsPage } from './admin/settings';
import { AdminMatchingPage } from './admin/matching';
import { AdminMatchingRunPage } from './admin/matching-run';
import { SingleParentQuestionnairePage } from './parent/single-parent-form';
import { ParentQuestionnairePage } from './parent';
import { ProfilePage } from './profile';
import { ParentUploadPhotosPage } from './parent/questionnaire-upload-photos';

export const Private = () => {
  const { user, isInternal, isSuperAdmin, isParent } = useAuth();

  useAuthSignOutSubscriber();
  useCardImagePreload();

  if (!user) {
    return <Loader isLoading />;
  }

  return (
    <MainLayout>
      <Routes>
        {isInternal ? (
          <>
            {/* Admin Routes */}
            {/* SAA-79 §3.6 — registered unconditionally, gated on the element: a conditional
                <Route> here falls through to /admin/:entity for Admin/Staff instead of the "*"
                fallback, rendering AdminUserList with entity==="settings" instead of redirecting. */}
            {/* SAA-150 — splat so `modules/settings` owns its own inner <Routes> (tabs, rule
                editor) without touching this file's route-ranking behaviour. The element gate
                above and the unconditional registration stay exactly as SAA-79 §3.6 describes. */}
            <Route
              path="/admin/settings/*"
              element={isSuperAdmin ? <AdminSettingsPage /> : <Navigate to="/admin/surrogates" replace />}
            />
            {/* SAA-84 — same unconditional-registration/element-gate pattern as the settings route
                above; enforcement is backend-side (ACL + capability error), this gate is UX-only. */}
            <Route
              path={MATCHING_ROUTE}
              element={
                resolvedTenantConfig.matchingToolEnabled ? (
                  <AdminMatchingPage />
                ) : (
                  <Navigate to="/admin/surrogates" replace />
                )
              }
            />
            <Route
              path={MATCHING_LOADER_ROUTE}
              element={
                resolvedTenantConfig.matchingToolEnabled ? (
                  <AdminMatchingRunPage />
                ) : (
                  <Navigate to="/admin/surrogates" replace />
                )
              }
            />
            {/* Any OTHER `/admin/matching/*` child (there are none yet) would otherwise rank onto
                `/admin/:entity/:id` and render a user-detail page for that id — bounce it back. */}
            <Route path={`${MATCHING_ROUTE}/*`} element={<Navigate to={MATCHING_ROUTE} replace />} />
            <Route path="/admin/:entity/" element={<AdminUsersPage />} />
            <Route path="/admin/:entity/:id" element={<AdminSingleUserPage />} />
            <Route path="/admin/:entity/:id/:survey" element={<AdminSingleUserFormPage />} />
            <Route path="*" element={<Navigate to="/admin/surrogates" />} />
          </>
        ) : isParent ? (
          <>
            {/* Parent Routes */}
            <Route path="/parent-questionnaire" element={<ParentQuestionnairePage />} />
            <Route path="/parent-questionnaire/upload-photos" element={<ParentUploadPhotosPage />} />
            <Route path="/parent-questionnaire/:survey" element={<SingleParentQuestionnairePage />} />
            <Route path="*" element={<Navigate to="/parent-questionnaire" />} />
          </>
        ) : (
          <>
            {/* Surrogate Routes */}
            <Route path="/surrogate-application" element={<SurrogateApplicationPage />} />
            <Route path="/surrogate-application/upload-photos" element={<UploadPhotosPage />} />
            <Route path="/surrogate-application/:survey" element={<UserFormPage />} />
            <Route path="*" element={<Navigate to="/surrogate-application" />} />
          </>
        )}
        {/* General Private Routes */}
        <Route path="/my-account" element={<ProfilePage />} />
      </Routes>
    </MainLayout>
  );
};
